import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Sparkles } from "lucide-react";
import { useI18n, LanguageToggle } from "@/i18n/I18nContext";

export default function MarketingNav() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const LINKS = [
    { label: t.nav.why, href: "/#why" },
    { label: t.nav.how, href: "/#how" },
    { label: t.nav.ai, href: "/#ai" },
    { label: t.nav.promotions, href: "/promotions" },
    { label: t.nav.pricing, href: "/pricing" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.querySelector(href.slice(1))?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.querySelector(href.slice(1))?.scrollIntoView({ behavior: "smooth" });
      }
    } else navigate(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#FDFBF9]/80 border-b border-[#2C3E35]/8" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-18 flex items-center justify-between py-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <img src="/bellaos-logo.png" alt="BellaOS" className="h-10 w-10 object-contain" />
          <span className="font-display font-extrabold text-xl tracking-tight text-[#0A0A0A]">BellaOS</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l, i) => (
            <button key={i} data-testid={`nav-link-${i}`}
              onClick={() => go(l.href)}
              className="text-sm font-medium text-[#2C3E35]/80 hover:text-[#0A0A0A] transition-colors">
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          <Link to="/login" data-testid="nav-signin" className="text-sm font-semibold text-[#2C3E35] hover:text-[#0A0A0A] transition-colors px-2">{t.nav.signin}</Link>
          <Link to="/register" data-testid="nav-cta-trial"
            className="text-sm font-semibold px-5 py-2.5 rounded-full bg-[#2C3E35] text-[#FDFBF9] hover:bg-[#0A0A0A] transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]">
            {t.nav.trial}
          </Link>
        </div>

        <button className="lg:hidden p-2 text-[#0A0A0A]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FDFBF9] border-t border-[#2C3E35]/8 px-5 py-4 space-y-2">
          {LINKS.map((l, i) => (
            <button key={i} onClick={() => go(l.href)} className="block w-full text-left py-2 text-sm font-medium text-[#2C3E35]">{l.label}</button>
          ))}
          <div className="py-2"><LanguageToggle /></div>
          <div className="flex gap-3 pt-1">
            <Link to="/login" className="flex-1 text-center py-2.5 rounded-full border border-[#2C3E35]/15 text-sm font-semibold">{t.nav.signin}</Link>
            <Link to="/register" className="flex-1 text-center py-2.5 rounded-full bg-[#2C3E35] text-[#FDFBF9] text-sm font-semibold">{t.nav.trial}</Link>
          </div>
        </div>
      )}
    </motion.header>
  );
}
